'use client';

import React from 'react';
import Image from 'next/image';

import PrimaryButton from '@/components/ui/Button/PrimaryButton';
import RichText from '@/components/ui/RichText/RichText';
import MediaLogoGrid from '@/components/ui/MediaLogoGrid/MediaLogoGrid';
import BalancedContentSection from '@/components/sections/BalancedContentSection/BalancedContentSection';
import { getMediaUrl } from '@/components/sections/BalancedContentSection/balancedContentSectionHelpers';
import { scrollToElement } from '@/utils';

export interface MediaWithTextProps {
  image?: { url?: string; title?: string };
  heading?: string;
  description?: string;
  subItems?: {
    heading?: string;
    description?: string;
    subDescription?: string;
  }[];
  imagesCollection?: {
    items?: { title?: string; url?: string }[];
  };
  variant?: 'standard' | 'info-card';
  imagePosition?: 'left' | 'right';
  imageMode?: 'cover' | 'natural';
  showButton?: boolean;
  isCenter?: boolean;
  customClasses?: {
    mediaBox?: string;
    heading?: string;
    description?: string;
  };
  backgroundColor?: string;
  imageRatio?: 'lg:w-1/2' | 'lg:w-2/5' | 'lg:w-[45%]';
  reverseOnMobile?: boolean;
  isDraft?: boolean;
}

export const MediaWithText: React.FC<MediaWithTextProps> = ({
  image,
  heading,
  description,
  subItems = [],
  imagesCollection,
  variant = 'standard',
  imagePosition = 'left',
  imageMode = 'cover',
  showButton = false,
  isCenter = false,
  customClasses,
  backgroundColor,
  imageRatio,
  reverseOnMobile,
  isDraft,
}) => {
  if (!image && !heading && !description) {
    return null;
  }

  const mediaContent = (
    <div className="flex w-full flex-col justify-center">
      <div
        className={`w-full overflow-hidden rounded-xl shadow-md ${
          imageMode === 'cover' ? 'relative h-125' : 'flex justify-center'
        } ${customClasses?.mediaBox || ''}`}
      >
        {image?.url && (
          <Image
            src={image.url}
            alt={image.title || ''}
            {...(imageMode === 'cover'
              ? { fill: true, className: 'object-cover' }
              : {
                  width: 800,
                  height: 1000,
                  className: 'h-auto max-w-full object-contain',
                })}
          />
        )}
      </div>

      {variant === 'info-card' && subItems.length > 0 && (
        <div className="mt-4 space-y-4">
          <div className="bg-bg-secondary rounded-xl border border-gray-100 p-4">
            <RichText
              styles={{
                p: `text-text-main text-base ${customClasses?.description || ''}`,
              }}
            >
              {subItems[0]?.description ?? ''}
            </RichText>
          </div>
          <RichText styles={{ p: 'text-theme-primary text-sm font-bold' }}>
            {subItems[0]?.subDescription ?? ''}
          </RichText>
        </div>
      )}
    </div>
  );

  const mediaLogos = (imagesCollection?.items ?? []).map((item: any) => ({
    name: item.title,
    logo: item.url,
    url: getMediaUrl(item.title),
  }));

  const textContent = (
    <div className={isCenter ? 'flex flex-col items-center text-center' : ''}>
      <RichText
        styles={{
          p: `rich-text-heading text-theme-brand-blue mb-4 ${customClasses?.heading || ''}`,
        }}
      >
        {heading ?? ''}
      </RichText>
      <RichText
        styles={{
          p: `text-lg text-text-secondary leading-relaxed [&_blockquote>p]:text-theme-primary [&_blockquote>p]:font-bold [&_blockquote>p]:text-heading-sm md:[&_blockquote>p]:text-heading-md ${customClasses?.description || ''}`,
        }}
      >
        {description ?? ''}
      </RichText>

      {showButton && (
        <PrimaryButton
          className="mt-6"
          data={{ title: 'Enquire Now', functionality: 'show_form' }}
          onShowForm={() => scrollToElement('form')}
          widthFitContent
        />
      )}

      {mediaLogos.length > 0 && <MediaLogoGrid items={mediaLogos} />}
    </div>
  );

  const balancedData = {
    heading,
    description,
    textPosition: imagePosition === 'left' ? 'right' : ('left' as const),
    backgroundColor,
    imageRatio,
    reverseOnMobile,
    isDraft,
  };

  return (
    <BalancedContentSection
      data={balancedData}
      mediaContent={mediaContent}
      textContent={textContent}
      spacingClassName="py-8 md:py-10"
      gapClassName="gap-12 xl:gap-24"
    />
  );
};
